import { useState, useRef, useCallback, useEffect } from 'react';
import { getAccessToken, isTokenExpired, refreshToken, redirectToLogin } from '../utils/auth';

const SUPPORTED_MIME_TYPES = [
  'audio/webm;codecs=opus',
  'audio/webm',
  'audio/ogg;codecs=opus',
  'audio/ogg',
  'audio/mp4',
];

function getSupportedMimeType() {
  if (typeof MediaRecorder === 'undefined') return null;
  for (const type of SUPPORTED_MIME_TYPES) {
    if (MediaRecorder.isTypeSupported(type)) return type;
  }
  return null;
}

function getFileExtension(mimeType) {
  if (mimeType.startsWith('audio/webm')) return 'webm';
  if (mimeType.startsWith('audio/ogg')) return 'ogg';
  if (mimeType.startsWith('audio/mp4')) return 'm4a';
  return 'webm';
}

async function transcribeAudio(audioBlob, fileName) {
  if (isTokenExpired()) {
    const refreshed = await refreshToken();
    if (!refreshed) {
      redirectToLogin();
      throw new Error('Authentication expired');
    }
  }

  const formData = new FormData();
  formData.append('file', audioBlob, fileName);

  const response = await fetch('/api/transcribe', {
    method: 'POST',
    headers: { Authorization: `Bearer ${getAccessToken()}` },
    credentials: 'omit',
    body: formData,
  });

  if (!response.ok) {
    const errorData = await response.json().catch(() => ({}));
    throw new Error(errorData.detail || `Transcription failed (${response.status})`);
  }

  const data = await response.json();
  return data.text;
}

function checkVoiceSupport() {
  if (typeof navigator === 'undefined' || typeof MediaRecorder === 'undefined') {
    return 'Voice input is not supported in this browser.';
  }
  if (typeof navigator.mediaDevices === 'undefined' || typeof navigator.mediaDevices.getUserMedia !== 'function') {
    return 'Voice input requires a secure connection (HTTPS). Please use HTTPS or localhost.';
  }
  if (!getSupportedMimeType()) {
    return 'No supported audio format found in this browser.';
  }
  return null;
}

const useVoiceRecorder = () => {
  const [isRecording, setIsRecording] = useState(false);
  const [isTranscribing, setIsTranscribing] = useState(false);
  const [recordingDuration, setRecordingDuration] = useState(0);
  const [error, setError] = useState(null);

  const mediaRecorderRef = useRef(null);
  const streamRef = useRef(null);
  const chunksRef = useRef([]);
  const timerRef = useRef(null);
  const mimeTypeRef = useRef(null);

  const cleanup = useCallback(() => {
    if (timerRef.current) {
      clearInterval(timerRef.current);
      timerRef.current = null;
    }
    if (mediaRecorderRef.current && mediaRecorderRef.current.state !== 'inactive') {
      mediaRecorderRef.current.stop();
    }
    if (streamRef.current) {
      streamRef.current.getTracks().forEach(track => track.stop());
      streamRef.current = null;
    }
    mediaRecorderRef.current = null;
    chunksRef.current = [];
  }, []);

  useEffect(() => {
    return cleanup;
  }, [cleanup]);

  const startRecording = useCallback(async () => {
    setError(null);

    const supportError = checkVoiceSupport();
    if (supportError) {
      setError(supportError);
      return;
    }

    const mimeType = getSupportedMimeType();
    mimeTypeRef.current = mimeType;

    try {
      const stream = await navigator.mediaDevices.getUserMedia({ audio: true });
      streamRef.current = stream;
      chunksRef.current = [];

      const recorder = new MediaRecorder(stream, { mimeType });
      mediaRecorderRef.current = recorder;

      recorder.ondataavailable = (e) => {
        if (e.data.size > 0) {
          chunksRef.current.push(e.data);
        }
      };

      recorder.start(250);
      setIsRecording(true);
      setRecordingDuration(0);

      timerRef.current = setInterval(() => {
        setRecordingDuration(prev => prev + 1);
      }, 1000);
    } catch (err) {
      cleanup();
      if (err.name === 'NotAllowedError') {
        setError('Microphone access denied. Please allow microphone access and try again.');
      } else if (err.name === 'NotFoundError') {
        setError('No microphone found. Please connect a microphone and try again.');
      } else {
        setError('Failed to start recording. Please try again.');
      }
    }
  }, [cleanup]);

  const stopRecording = useCallback(() => {
    return new Promise((resolve) => {
      if (!mediaRecorderRef.current || mediaRecorderRef.current.state === 'inactive') {
        resolve(null);
        return;
      }

      if (timerRef.current) {
        clearInterval(timerRef.current);
        timerRef.current = null;
      }

      mediaRecorderRef.current.onstop = async () => {
        setIsRecording(false);
        const mimeType = mimeTypeRef.current;
        const blob = new Blob(chunksRef.current, { type: mimeType });

        if (streamRef.current) {
          streamRef.current.getTracks().forEach(track => track.stop());
          streamRef.current = null;
        }
        mediaRecorderRef.current = null;
        chunksRef.current = [];

        if (blob.size === 0) {
          setError('No audio recorded. Please try again.');
          resolve(null);
          return;
        }

        setIsTranscribing(true);
        setError(null);

        try {
          const ext = getFileExtension(mimeType);
          const text = await transcribeAudio(blob, `recording.${ext}`);
          setIsTranscribing(false);
          resolve(text);
        } catch (err) {
          setIsTranscribing(false);
          setError(err.message || 'Transcription failed. Please try again.');
          resolve(null);
        }
      };

      mediaRecorderRef.current.stop();
    });
  }, []);

  const cancelRecording = useCallback(() => {
    cleanup();
    setIsRecording(false);
    setRecordingDuration(0);
    setError(null);
  }, [cleanup]);

  const clearError = useCallback(() => {
    setError(null);
  }, []);

  return {
    isRecording,
    isTranscribing,
    recordingDuration,
    error,
    startRecording,
    stopRecording,
    cancelRecording,
    clearError,
  };
};

export default useVoiceRecorder;
